import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router';
import { Bell, MapPin, QrCode, Smartphone, Trophy } from 'lucide-react';
import { PANEL_URL } from '../lib/api';
import { TurnivoLogo } from '../components/TurnivoLogo';
import '../styles/landing-cinematic.css';

const REVEAL_SELECTOR =
  '.section-heading,.feature-card,.demo-copy,.demo-board,.pricing-grid,.trial-banner,.final-cta>.eyebrow,.final-cta>div,.footer-top,.app-section';

const TEAM_NAME = { a: 'Grom Warszawa', b: 'Ace Kraków' } as const;

type Team = keyof typeof TEAM_NAME;
type Scores = Record<Team, number>;

const INITIAL_SCORES: Scores = { a: 18, b: 16 };

const saveData = Boolean(
  (navigator as Navigator & { connection?: { saveData?: boolean } }).connection?.saveData,
);

const isSetFinished = (scores: Scores) =>
  Math.max(scores.a, scores.b) >= 25 && Math.abs(scores.a - scores.b) >= 2;

export default function LandingPage() {
  const media = useMemo(
    () => ({
      reduce: window.matchMedia('(prefers-reduced-motion: reduce)'),
      compact: window.matchMedia('(max-height: 800px) and (max-width: 640px)'),
    }),
    [],
  );

  const [motionPaused, setMotionPaused] = useState(() => media.reduce.matches);
  const [menuOpen, setMenuOpen] = useState(false);
  const [filmReady, setFilmReady] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [scores, setScores] = useState<Scores>(INITIAL_SCORES);
  const [history, setHistory] = useState<Scores[]>([]);
  const [message, setMessage] = useState('Kliknij, aby dodać punkt');
  const [pop, setPop] = useState({ a: 0, b: 0 });

  const rootRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLElement>(null);
  const heroRef = useRef<HTMLElement>(null);
  const filmRef = useRef<HTMLVideoElement>(null);
  const journeyRef = useRef<HTMLElement>(null);
  const journeyDisplayRef = useRef<HTMLDivElement>(null);
  const demoRef = useRef<HTMLElement>(null);
  const finalRef = useRef<HTMLElement>(null);
  const progressRef = useRef<HTMLDivElement>(null);

  const manuallyPausedRef = useRef(false);
  const heroVisibleRef = useRef(true);
  const filmLoadedRef = useRef(false);
  const scrollQueuedRef = useRef(false);

  const finished = isSetFinished(scores);
  const statusText = finished
    ? `Set dla ${scores.a > scores.b ? TEAM_NAME.a : TEAM_NAME.b}!`
    : message;

  const storyIsSticky = useCallback(
    () => !media.compact.matches && !media.reduce.matches,
    [media],
  );

  /* ---------- hero film ---------- */

  const syncFilm = useCallback(() => {
    const film = filmRef.current;
    if (!film) return;
    if (motionPaused || !heroVisibleRef.current || document.hidden || (saveData && !filmLoadedRef.current)) {
      film.pause();
      return;
    }
    if (!filmLoadedRef.current) {
      const source = film.querySelector('source');
      if (source?.dataset.src) {
        source.src = source.dataset.src;
        film.load();
      }
      filmLoadedRef.current = true;
    }
    const played = film.play();
    if (played?.catch) played.catch(() => {});
  }, [motionPaused]);

  const syncFilmRef = useRef(syncFilm);
  syncFilmRef.current = syncFilm;

  /* ---------- scroll driven effects ---------- */

  const updateScroll = useCallback(() => {
    scrollQueuedRef.current = false;

    const total = document.documentElement.scrollHeight - window.innerHeight;
    const progress = total > 0 ? Math.min(1, Math.max(0, window.scrollY / total)) : 0;
    if (progressRef.current) {
      progressRef.current.style.transform = `scaleX(${progress})`;
    }

    const journey = journeyRef.current;
    const header = headerRef.current;
    if (journey && header && storyIsSticky()) {
      const headerHeight = header.offsetHeight;
      const rect = journey.getBoundingClientRect();
      const distance = Math.max(1, journey.offsetHeight - (window.innerHeight - headerHeight));
      const p = Math.max(0, Math.min(0.999, (headerHeight - rect.top) / distance));
      const step = Math.floor(p * 3);
      setActiveStep((current) => (current === step ? current : step));
      if (!motionPaused && journeyDisplayRef.current) {
        journeyDisplayRef.current.style.setProperty('--tour-rotate', `${-6 + p * 12}deg`);
      }
    }

    if (motionPaused) return;

    const demo = demoRef.current;
    if (demo) {
      const top = demo.getBoundingClientRect().top;
      demo.style.setProperty('--demo-radius', `${Math.max(0, Math.min(65, top * 0.09))}px`);
    }

    const final = finalRef.current;
    if (final) {
      const rect = final.getBoundingClientRect();
      if (rect.top < window.innerHeight && rect.bottom > 0) {
        const p = Math.max(
          0,
          Math.min(1, (window.innerHeight - rect.top) / (window.innerHeight + rect.height)),
        );
        final.style.setProperty('--final-scale', String(0.94 + p * 0.09));
      }
    }
  }, [motionPaused, storyIsSticky]);

  const updateScrollRef = useRef(updateScroll);
  updateScrollRef.current = updateScroll;

  useEffect(() => {
    const onScroll = () => {
      if (scrollQueuedRef.current) return;
      scrollQueuedRef.current = true;
      requestAnimationFrame(() => updateScrollRef.current());
    };
    const onResize = () => requestAnimationFrame(() => updateScrollRef.current());
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onResize);
    };
  }, []);

  /* ---------- motion preferences ---------- */

  useEffect(() => {
    const onChange = () => setMotionPaused(media.reduce.matches || manuallyPausedRef.current);
    media.reduce.addEventListener('change', onChange);
    return () => media.reduce.removeEventListener('change', onChange);
  }, [media]);

  useEffect(() => {
    syncFilm();
    updateScroll();
  }, [syncFilm, updateScroll]);

  useEffect(() => {
    const onVisibility = () => syncFilmRef.current();
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, []);

  // Some rules in landing-cinematic.css were authored against <body>; keep both in sync.
  useEffect(() => {
    document.body.classList.toggle('paused', motionPaused);
    document.body.classList.toggle('motion-ready', !motionPaused);
    return () => {
      document.body.classList.remove('paused');
      document.body.classList.remove('motion-ready');
    };
  }, [motionPaused]);

  const toggleMotion = () => {
    const next = !motionPaused;
    manuallyPausedRef.current = next;
    setMotionPaused(next);
  };

  /* ---------- reveals + hero visibility ---------- */

  useEffect(() => {
    const root = rootRef.current;
    if (!root || !('IntersectionObserver' in window)) return;

    const items = Array.from(root.querySelectorAll<HTMLElement>(REVEAL_SELECTOR));
    items.forEach((el, i) => {
      el.classList.add('reveal');
      if (el.classList.contains('feature-card')) {
        el.style.setProperty('--reveal-delay', `${(i % 3) * 100}ms`);
      }
    });

    const revealObserver = new IntersectionObserver(
      (entries) =>
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('visible');
          revealObserver.unobserve(entry.target);
        }),
      { threshold: 0.1, rootMargin: '0px 0px -25px 0px' },
    );
    items.forEach((el) => revealObserver.observe(el));

    let heroObserver: IntersectionObserver | undefined;
    if (heroRef.current) {
      heroObserver = new IntersectionObserver(
        (entries) => {
          heroVisibleRef.current = entries[0].isIntersecting;
          syncFilmRef.current();
        },
        { threshold: 0.03 },
      );
      heroObserver.observe(heroRef.current);
    }

    return () => {
      revealObserver.disconnect();
      heroObserver?.disconnect();
    };
  }, []);

  /* ---------- mobile menu ---------- */

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, []);

  /* ---------- referee demo ---------- */

  const addPoint = (team: Team) => {
    if (finished) return;
    setHistory((prev) => [...prev, scores]);
    setScores((prev) => ({
      a: prev.a + (team === 'a' ? 1 : 0),
      b: prev.b + (team === 'b' ? 1 : 0),
    }));
    setMessage(`Punkt dla ${TEAM_NAME[team]}`);
    setPop((prev) => ({
      a: prev.a + (team === 'a' ? 1 : 0),
      b: prev.b + (team === 'b' ? 1 : 0),
    }));
  };

  const undoPoint = () => {
    if (!history.length) return;
    setScores(history[history.length - 1]);
    setHistory((prev) => prev.slice(0, -1));
    setMessage('Ostatni punkt cofnięty');
  };

  const resetDemo = () => {
    setScores(INITIAL_SCORES);
    setHistory([]);
    setMessage('Kliknij, aby dodać punkt');
  };

  /* ---------- journey ---------- */

  const goToStep = (index: number) => {
    setActiveStep(index);
    if (!storyIsSticky()) return;
    const journey = journeyRef.current;
    const header = headerRef.current;
    if (!journey || !header) return;
    const headerHeight = header.offsetHeight;
    const start = journey.getBoundingClientRect().top + window.scrollY - headerHeight;
    const distance = Math.max(1, journey.offsetHeight - (window.innerHeight - headerHeight));
    window.scrollTo({
      top: start + (distance * (index + 0.18)) / 3,
      behavior: motionPaused ? 'instant' : 'smooth',
    });
  };

  const scoreClass = (team: Team) => (!motionPaused && pop[team] > 0 ? 'score-pop' : undefined);

  return (
    <div
      ref={rootRef}
      className={`landing-cinematic ${motionPaused ? 'paused' : 'motion-ready'}`}
    >
      <div ref={progressRef} className="scroll-progress" aria-hidden="true" />

      <button
        className="motion-control"
        id="motion-toggle"
        type="button"
        aria-pressed={motionPaused}
        aria-label={motionPaused ? 'Włącz animacje i film' : 'Zatrzymaj animacje i film'}
        onClick={toggleMotion}
      >
        <span aria-hidden="true">{motionPaused ? '▶' : 'Ⅱ'}</span>{' '}
        <span className="motion-label">{motionPaused ? 'Włącz ruch' : 'Zatrzymaj ruch'}</span>
      </button>

      <a className="skip" href="#main">
        Przejdź do treści
      </a>

      <header className="header" ref={headerRef}>
        <div className="nav wrap">
          <TurnivoLogo className="brand brand-portal" />
          <nav className="desktop-nav" aria-label="Nawigacja główna">
            <a href="#funkcje">Możliwości</a>
            <a href="#aplikacja">Aplikacja</a>
            <a href="#jak-to-dziala">Jak to działa</a>
            <a href="#cennik">Cennik</a>
            <Link to="/turnieje">
              Turnieje <span aria-hidden="true">↗</span>
            </Link>
          </nav>
          <div className="nav-actions">
            <a href={`${PANEL_URL}/login`} className="login">
              Zaloguj się
            </a>
            <Link to="/register" className="button small">
              Zacznij teraz <span aria-hidden="true">↗</span>
            </Link>
            <button
              className="menu-toggle"
              type="button"
              aria-label={menuOpen ? 'Zamknij menu' : 'Otwórz menu'}
              aria-expanded={menuOpen}
              aria-controls="mobile-nav"
              onClick={() => setMenuOpen((open) => !open)}
            >
              ☰
            </button>
          </div>
        </div>
        <nav
          id="mobile-nav"
          className="mobile-nav"
          aria-label="Nawigacja mobilna"
          hidden={!menuOpen}
          onClick={() => setMenuOpen(false)}
        >
          <a href="#funkcje">Możliwości</a>
          <a href="#aplikacja">Aplikacja</a>
          <a href="#jak-to-dziala">Jak to działa</a>
          <a href="#cennik">Cennik</a>
          <Link to="/turnieje">Turnieje</Link>
          <a href={`${PANEL_URL}/login`}>Zaloguj się</a>
        </nav>
      </header>

      <main id="main">
        <section className="hero wrap cinematic-hero" ref={heroRef}>
          <div className="hero-copy">
            <div className="eyebrow">
              <span className="tiny-line" /> STWORZONE DLA SIATKÓWKI
            </div>
            <h1>
              TWÓJ TURNIEJ.
              <br />
              <span className="lime">
                NOWY
                <br />
                POZIOM.
              </span>
            </h1>
            <p>
              Ty tworzysz sportowe emocje.
              <br />
              My ogarniamy resztę — od pierwszego
              <br className="desktop-break" /> zgłoszenia po ostatni punkt.
            </p>
            <div className="hero-buttons">
              <Link className="button" to="/register">
                Stwórz swój turniej <span aria-hidden="true">↗</span>
              </Link>
              <a className="demo-link" href="#demo">
                <span className="play" aria-hidden="true">
                  ▶
                </span>{' '}
                Sprawdź, jak to działa
              </a>
            </div>
            <div className="hero-note">
              <span aria-hidden="true">✓</span> 3 dni za darmo{' '}
              <span className="note-divider" /> Bez karty kredytowej
            </div>
          </div>

          <div className="hero-visual">
            <div className="hero-photo">
              <img
                className="hero-poster"
                src="/hero/indoor-match-poster.jpg"
                alt="Zawodnicy grający w siatkówkę na hali sportowej"
                width={1280}
                height={720}
              />
              <video
                id="hero-film"
                ref={filmRef}
                className={filmReady ? 'hero-film ready' : 'hero-film'}
                muted
                loop
                playsInline
                preload="none"
                poster="/hero/indoor-match-poster.jpg"
                aria-hidden="true"
                tabIndex={-1}
                onPlaying={() => setFilmReady(true)}
                onError={() => setFilmReady(false)}
              >
                <source data-src="/hero/indoor-match.mp4" type="video/mp4" />
              </video>
              <div className="photo-top">
                <span className="outline-label">TURNIVO / POCZUJ GRĘ</span>
                <span className="photo-cross" aria-hidden="true">
                  +
                </span>
              </div>
              <span className="photo-index">NIECH LICZY SIĘ GRA.</span>
            </div>

            <div className="floating-score">
              <div className="score-top">
                <span>
                  <i className="status-dot" /> WYNIKI LIVE
                </span>
                <span>PODGLĄD DEMO</span>
              </div>
              <div className="score-match">
                <div>
                  <span className="team-avatar">GW</span>
                  <b>Grom Warszawa</b>
                </div>
                <strong>24</strong>
              </div>
              <div className="score-match">
                <div>
                  <span className="team-avatar alternate">AK</span>
                  <b>Ace Kraków</b>
                </div>
                <strong>22</strong>
              </div>
              <div className="score-bottom">
                <span>
                  Set 2 <span className="muted">/ Boisko 1</span>
                </span>
                <span className="lime">
                  Piłka setowa <span aria-hidden="true">↗</span>
                </span>
              </div>
            </div>
          </div>

          <a className="scroll-cue" href="#funkcje">
            <span className="scroll-track" aria-hidden="true" /> PRZEWIŃ. POCZUJ RÓŻNICĘ.
          </a>
        </section>

        <div className="kinetic-strip" aria-label="Mniej organizacji. Więcej gry.">
          <div className="kinetic-track" aria-hidden="true">
            {[0, 1, 2, 3].map((i) => (
              <span key={i} style={{ display: 'contents' }}>
                <span>MNIEJ ORGANIZACJI.</span>
                <span className="stroke-type">WIĘCEJ GRY.</span>
                <span className="kinetic-star">✳</span>
              </span>
            ))}
          </div>
        </div>

        <div className="benefit-strip">
          <div className="wrap strip-inner">
            <span>
              OD PIERWSZEGO GWIZDKA
              <br />
              <strong>WSZYSTKO POD KONTROLĄ.</strong>
            </span>
            <span>
              <span className="strip-symbol">↗</span> Automatyczny harmonogram
            </span>
            <span>
              <span className="strip-symbol">◎</span> Wyniki na żywo
            </span>
            <span>
              <span className="strip-symbol">⌘</span> Panel sędziego
            </span>
          </div>
        </div>

        <section className="section wrap" id="funkcje">
          <div className="section-heading">
            <div>
              <div className="eyebrow">01 / TWOJE CENTRUM DOWODZENIA</div>
              <h2>
                Cały turniej.
                <br />
                <span className="muted">Jedno miejsce.</span>
              </h2>
            </div>
            <p>
              Koniec z arkuszami, kartkami i pytaniami „kiedy gramy?”. Każdy wie, co dalej. Ty masz
              wszystko pod ręką.
            </p>
          </div>

          <div className="feature-grid">
            <article className="feature-card schedule-card">
              <span className="feature-num">01</span>
              <div className="mini-schedule" aria-label="Przykładowy harmonogram">
                <div className="schedule-title">
                  <span>Harmonogram</span>
                  <span className="mini-tag">3 BOISKA</span>
                </div>
                <div className="schedule-row">
                  <span>10:00</span>
                  <span>
                    <b>Grom Warszawa — Ace Kraków</b>
                    <small>Faza grupowa · Boisko 1</small>
                  </span>
                  <i className="status-dot" />
                </div>
                <div className="schedule-row">
                  <span>10:45</span>
                  <span>
                    <b>Blok Rzeszów — Siatka Gdańsk</b>
                    <small>Faza grupowa · Boisko 2</small>
                  </span>
                  <span className="schedule-arrow">↗</span>
                </div>
                <div className="schedule-row">
                  <span>11:30</span>
                  <span>
                    <b>Smash Wrocław — Crew Poznań</b>
                    <small>Faza grupowa · Boisko 3</small>
                  </span>
                  <span className="schedule-arrow">↗</span>
                </div>
              </div>
              <h3>Plan, który gra z Tobą.</h3>
              <p>
                Dodaj drużyny i boiska. Wygeneruj harmonogram, podziel zespoły na grupy i zapanuj nad
                kolejnością meczów.
              </p>
            </article>

            <article className="feature-card">
              <span className="feature-num">02</span>
              <div className="bracket-preview" aria-label="Przykładowa drabinka">
                <div className="bracket-round">
                  <div className="bracket-game">
                    <span>
                      Grom Warszawa <b>2</b>
                    </span>
                    <span>
                      Siatka Gdańsk <b>0</b>
                    </span>
                  </div>
                  <div className="bracket-game">
                    <span>
                      Ace Kraków <b>2</b>
                    </span>
                    <span>
                      Blok Rzeszów <b>1</b>
                    </span>
                  </div>
                </div>
                <div className="bracket-connector" aria-hidden="true" />
                <div className="bracket-final">
                  <span className="mini-label">FINAŁ</span>
                  <div className="bracket-game">
                    <span>Grom Warszawa</span>
                    <span>Ace Kraków</span>
                  </div>
                </div>
              </div>
              <h3>Od grupy do wielkiego finału.</h3>
              <p>
                Każdy z każdym, grupy i play-offy albo system pucharowy. Format dopasowany do Twojego
                turnieju.
              </p>
            </article>

            <article className="feature-card">
              <span className="feature-num">03</span>
              <div className="share-preview">
                <span className="share-icon" aria-hidden="true">
                  ↗
                </span>
                <span>Jeden link. Cały turniej.</span>
                <div className="share-address">
                  turnivo.pl/turnieje/… <span aria-hidden="true">↗</span>
                </div>
                <div className="audiences">
                  <span>Zawodnicy</span>
                  <span>Kibice</span>
                  <span>Sędziowie</span>
                </div>
              </div>
              <h3>Emocje dostępne dla każdego.</h3>
              <p>
                Udostępnij publiczny link. Wyniki, tabele i przebieg turnieju są zawsze pod ręką —
                także na telefonie.
              </p>
            </article>
          </div>
        </section>

        <section className="demo-section" id="demo" ref={demoRef}>
          <div className="wrap demo-layout">
            <div className="demo-copy">
              <div className="eyebrow">
                <i className="status-dot" /> KAŻDY PUNKT MA ZNACZENIE
              </div>
              <h2>
                Boisko żyje.
                <br />
                <span className="lime">Wynik nadąża.</span>
              </h2>
              <p>
                Sędzia dodaje punkt. Kibice widzą wynik.
                <br />
                Bez przepisywania, odświeżania i opóźnień w komunikacji.
              </p>
              <Link className="text-link" to="/referee-demo">
                Otwórz pełne demo sędziego <span aria-hidden="true">↗</span>
              </Link>
              <div className="demo-hint">
                <span aria-hidden="true">↳</span>
                <div>
                  Wypróbuj punktację obok.
                  <br />
                  To tylko demonstracja — możesz śmiało klikać.
                </div>
              </div>
            </div>

            <div className="demo-board">
              <div className="board-top">
                <span>
                  <i className="status-dot" /> PANEL SĘDZIEGO
                </span>
                <span className="mini-tag">DEMO</span>
              </div>
              <div className="board-title">
                Grom Warszawa <span>vs</span> Ace Kraków
              </div>
              <div className="board-subtitle">Boisko 1 · Set 1 · do 25 punktów</div>
              <div className="point-panels">
                <div className="point-panel">
                  <span>GROM WARSZAWA</span>
                  <strong id="score-a" key={`a-${pop.a}`} className={scoreClass('a')}>
                    {scores.a}
                  </strong>
                  <button
                    type="button"
                    className="point-button"
                    disabled={finished}
                    onClick={() => addPoint('a')}
                  >
                    + Punkt
                  </button>
                </div>
                <div className="point-separator">:</div>
                <div className="point-panel">
                  <span>ACE KRAKÓW</span>
                  <strong id="score-b" key={`b-${pop.b}`} className={scoreClass('b')}>
                    {scores.b}
                  </strong>
                  <button
                    type="button"
                    className="point-button secondary"
                    disabled={finished}
                    onClick={() => addPoint('b')}
                  >
                    + Punkt
                  </button>
                </div>
              </div>
              <div className="board-bottom">
                <button type="button" id="undo" disabled={!history.length} onClick={undoPoint}>
                  ↶ Cofnij punkt
                </button>
                <span id="demo-status" role="status" aria-live="polite">
                  {statusText}
                </span>
                <button
                  type="button"
                  id="reset"
                  aria-label="Zresetuj wynik demonstracyjny"
                  onClick={resetDemo}
                >
                  Reset
                </button>
              </div>
            </div>
          </div>
        </section>

        <section className="section wrap app-section" id="aplikacja">
          <div className="section-heading">
            <div>
              <div className="eyebrow">02 / TURNIVO W KIESZENI</div>
              <h2>
                Aplikacja mobilna.
                <br />
                <span className="muted">Turniej zawsze przy Tobie.</span>
              </h2>
            </div>
            <p>
              Organizator, sędzia, zawodnik i kibic — każdy ma swój widok. Wyniki live, nawigacja na
              boisko i skan QR turnieju bez szukania linków.
            </p>
          </div>

          <div className="feature-grid">
            <article className="feature-card">
              <span className="feature-num">01</span>
              <div className="share-preview" style={{ height: 160 }}>
                <span className="share-icon" aria-hidden="true">
                  <Smartphone size={28} />
                </span>
                <span>Trzy role w jednej apce</span>
                <div className="audiences">
                  <span>Organizator</span>
                  <span>Sędzia</span>
                  <span>Zawodnik</span>
                  <span>Kibic</span>
                </div>
              </div>
              <h3>Jedna aplikacja. Cztery perspektywy.</h3>
              <p>
                Po zalogowaniu wybierasz tryb: zarządzanie turniejem, sędziowanie na żywo, mecze
                swojej drużyny albo publiczny podgląd — bez przełączania między stronami.
              </p>
            </article>

            <article className="feature-card">
              <span className="feature-num">02</span>
              <div className="share-preview" style={{ height: 160 }}>
                <span className="share-icon" aria-hidden="true">
                  <Trophy size={28} />
                </span>
                <span>Panel sędziego w telefonie</span>
                <div className="audiences">
                  <span>Punkty</span>
                  <span>Sety</span>
                  <span>Walkower</span>
                  <span>Wynik ręczny</span>
                </div>
              </div>
              <h3>Sędziuj z boiska. Wynik od razu online.</h3>
              <p>
                Dodawaj punkty set po secie albo wpisz gotowy wynik zgodnie z zasadami turnieju.
                Kibice i zawodnicy widzą aktualizację w czasie rzeczywistym.
              </p>
            </article>

            <article className="feature-card">
              <span className="feature-num">03</span>
              <div className="share-preview" style={{ height: 160 }}>
                <span className="share-icon" aria-hidden="true">
                  <QrCode size={28} />
                </span>
                <span>Szybki dostęp do turnieju</span>
                <div className="audiences">
                  <span>QR</span>
                  <span>Nawigacja</span>
                  <span>Powiadomienia</span>
                </div>
              </div>
              <h3>Skan QR, mapa i powiadomienia.</h3>
              <p>
                Zeskanuj kod turnieju, otwórz nawigację do hali i trzymaj wyniki pod ręką. Idealne,
                gdy jesteś na obiekcie i nie chcesz szukać niczego w przeglądarce.
              </p>
            </article>
          </div>

          <div
            className="trial-banner"
            style={{ marginTop: 28, background: '#0F172A', borderColor: '#1E3A4F' }}
          >
            <div>
              <strong>Pobierz Turnivo na telefon</strong>
              <span>
                Android (APK) i iOS — ten sam system co panel webowy. Wyniki live, mecze, turnieje w
                pobliżu i tryb sędziego.
              </span>
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 10,
                  marginTop: 12,
                  color: '#94A3B8',
                  fontSize: 12,
                }}
              >
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  <Bell size={14} color="#00C7B1" /> Powiadomienia o meczach
                </span>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  <MapPin size={14} color="#00C7B1" /> Turnieje w pobliżu
                </span>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  <QrCode size={14} color="#00C7B1" /> Skan QR turnieju
                </span>
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, minWidth: 180 }}>
              <a
                href="/app/turnivo.apk"
                download
                className="button"
                style={{ gap: 12, padding: '14px 18px', fontSize: 13 }}
              >
                Android · APK <span aria-hidden="true">↓</span>
              </a>
              <a
                href="/app/turnivo-ios.tar.gz"
                download
                className="button outline"
                style={{ gap: 12, padding: '14px 18px', fontSize: 13 }}
              >
                iOS · paczka <span aria-hidden="true">↓</span>
              </a>
            </div>
          </div>
        </section>

<section
          className="journey"
          id="jak-to-dziala"
          aria-label="Jak działa Turnivo"
          ref={journeyRef}
        >
          <div className="journey-sticky">
            <div className="wrap journey-inner">
              <div className="journey-heading">
              <div className="eyebrow">03 / OD POMYSŁU DO PIERWSZEGO SERWISU</div>
                <h2>
                  Twój turniej.
                  <br />
                  <span className="muted">W trzech ruchach.</span>
                </h2>
                <p className="journey-intro">
                  Przewijaj i zobacz, jak pomysł zamienia się w gotowe rozgrywki.
                </p>
                <div className="journey-copy">
                  <article
                    className={activeStep === 0 ? 'journey-step active' : 'journey-step'}
                    aria-hidden={activeStep !== 0}
                  >
                    <span>01 / ZAPLANUJ</span>
                    <h3>
                      Wszystko zaczyna
                      <br />
                      się od Twoich zasad.
                    </h3>
                    <p>
                      Wybierz format, dodaj drużyny i boiska. Turnivo pomoże ułożyć plan rozgrywek.
                    </p>
                  </article>
                  <article
                    className={activeStep === 1 ? 'journey-step active' : 'journey-step'}
                    aria-hidden={activeStep !== 1}
                  >
                    <span>02 / GRAJ</span>
                    <h3>
                      Punkt po punkcie.
                      <br />
                      Wszyscy na bieżąco.
                    </h3>
                    <p>
                      Sędzia prowadzi mecz. Wynik trafia do zawodników i kibiców śledzących publiczny
                      podgląd.
                    </p>
                  </article>
                  <article
                    className={activeStep === 2 ? 'journey-step active' : 'journey-step'}
                    aria-hidden={activeStep !== 2}
                  >
                    <span>03 / ŚWIĘTUJ</span>
                    <h3>
                      Ostatni gwizdek.
                      <br />
                      Wielkie emocje.
                    </h3>
                    <p>
                      Od fazy grupowej do finału. Wyniki i końcowa klasyfikacja pozostają w jednym
                      miejscu.
                    </p>
                  </article>
                </div>
                <div className="journey-dots" aria-label="Etapy prezentacji">
                  {[
                    { label: 'Pokaż etap 1: planowanie', text: '01' },
                    { label: 'Pokaż etap 2: mecz', text: '02' },
                    { label: 'Pokaż etap 3: wyniki', text: '03' },
                  ].map((dot, index) => (
                    <button
                      key={dot.text}
                      type="button"
                      className={activeStep === index ? 'active' : undefined}
                      aria-label={dot.label}
                      aria-current={activeStep === index ? 'step' : undefined}
                      onClick={() => goToStep(index)}
                    >
                      <span>{dot.text}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div className="journey-display" ref={journeyDisplayRef}>
                <div className="stage-top">
                  <TurnivoLogo
                    asLink={false}
                    className="brand brand-portal brand-compact"
                    boxSize="h-8 w-8"
                    iconSize="w-4 h-4"
                    textSize="text-base"
                  />
                  <span>PODGLĄD DEMO</span>
                </div>
                <div className="tour-scenes">
                  <div
                    className={activeStep === 0 ? 'tour-scene active' : 'tour-scene'}
                    aria-hidden={activeStep !== 0}
                  >
                    <div className="tour-kicker">TWÓJ DZIEŃ MECZOWY</div>
                    <h3>Letni Puchar Siatkówki</h3>
                    <div className="tour-badges">
                      <span>8 drużyn</span>
                      <span>2 boiska</span>
                      <span>Grupy + play-off</span>
                    </div>
                    <div className="tour-table">
                      <div>
                        <span>10:00</span>
                        <b>Grom Warszawa — Ace Kraków</b>
                        <span>Boisko 1</span>
                      </div>
                      <div>
                        <span>10:00</span>
                        <b>Blok Rzeszów — Siatka Gdańsk</b>
                        <span>Boisko 2</span>
                      </div>
                      <div>
                        <span>10:45</span>
                        <b>Smash Wrocław — Crew Poznań</b>
                        <span>Boisko 1</span>
                      </div>
                    </div>
                    <div className="tour-message">✓ Harmonogram gotowy. Czas na grę.</div>
                  </div>

                  <div
                    className={activeStep === 1 ? 'tour-scene active' : 'tour-scene'}
                    aria-hidden={activeStep !== 1}
                  >
                    <div className="tour-kicker">
                      <i className="status-dot" /> TRWA MECZ · SET 2
                    </div>
                    <h3>Każdy punkt. Na żywo.</h3>
                    <div className="tour-match">
                      <div>
                        <span>Grom Warszawa</span>
                        <strong className="tour-score">24</strong>
                      </div>
                      <span>:</span>
                      <div>
                        <span>Ace Kraków</span>
                        <strong>22</strong>
                      </div>
                    </div>
                    <div className="tour-message">Piłka setowa dla Grom Warszawa</div>
                    <div className="tour-activity">
                      <span>+1 Grom Warszawa</span>
                      <span>Wynik zaktualizowany</span>
                    </div>
                  </div>

                  <div
                    className={activeStep === 2 ? 'tour-scene active' : 'tour-scene'}
                    aria-hidden={activeStep !== 2}
                  >
                    <div className="tour-kicker">TURNIEJ ZAKOŃCZONY</div>
                    <h3>Tak wygląda zwycięstwo.</h3>
                    <div className="winner-mark" aria-hidden="true">
                      01
                    </div>
                    <div className="winner-name">GROM WARSZAWA</div>
                    <div className="winner-label">ZWYCIĘZCA TURNIEJU</div>
                    <div className="ranking-line">
                      <span>02 &nbsp; Ace Kraków</span>
                      <span>03 &nbsp; Blok Rzeszów</span>
                    </div>
                  </div>
                </div>
                <div className="tour-bottom">
                  <span>OD PIERWSZEGO MECZU DO FINAŁU</span>
                  <span className="tour-page">{`0${activeStep + 1} / 03`}</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="pricing-section" id="cennik">
          <div className="wrap">
            <div className="section-heading">
              <div>
                <div className="eyebrow">04 / MAŁY TURNIEJ CZY WIELKA LIGA?</div>
                <h2>
                  Twój format.
                  <br />
                  <span className="muted">Twój plan.</span>
                </h2>
              </div>
              <p>
                Od lokalnych rozgrywek po regularne ligi.
                <br />
                Wybierz plan na swoją skalę.
              </p>
            </div>

            <div className="pricing-grid">
              <article className="price-card">
                <div className="plan-top">
                  <h3>Basic</h3>
                  <span>NA DOBRY START</span>
                </div>
                <div className="price">
                  29 <span>zł / mies.</span>
                </div>
                <p>Wszystko, czego potrzebuje mały turniej.</p>
                <Link className="button outline" to="/register">
                  Wybierz Basic <span>↗</span>
                </Link>
                <ul>
                  <li>1 aktywny turniej</li>
                  <li>Do 8 drużyn</li>
                  <li>Wyniki live i panel sędziego</li>
                  <li>Publiczny link, drabinka i tabele</li>
                </ul>
              </article>

              <article className="price-card recommended">
                <div className="recommended-label">NAJLEPSZA WARTOŚĆ</div>
                <div className="plan-top">
                  <h3>Optimum</h3>
                  <span>DLA ORGANIZATORÓW</span>
                </div>
                <div className="price">
                  49 <span>zł / mies.</span>
                </div>
                <p>Więcej drużyn. Więcej możliwości.</p>
                <Link className="button" to="/register">
                  Wybierz Optimum <span>↗</span>
                </Link>
                <ul>
                  <li>5 aktywnych turniejów</li>
                  <li>Do 32 drużyn na turniej</li>
                  <li>Wszystko z planu Basic</li>
                  <li>Tryb ligi, raporty PDF i CSV</li>
                  <li>Zarządzanie sędziami</li>
                </ul>
              </article>

              <article className="price-card">
                <div className="plan-top">
                  <h3>Premium</h3>
                  <span>PEŁNIA MOŻLIWOŚCI</span>
                </div>
                <div className="price">
                  99 <span>zł / mies.</span>
                </div>
                <p>Dla tych, którzy grają na większą skalę.</p>
                <Link className="button outline" to="/register">
                  Wybierz Premium <span>↗</span>
                </Link>
                <ul>
                  <li>Nieograniczona liczba turniejów</li>
                  <li>Nieograniczona liczba drużyn</li>
                  <li>Wszystko z planu Optimum</li>
                  <li>Własny branding i dostęp do API</li>
                  <li>Dedykowany opiekun</li>
                </ul>
              </article>
            </div>

            <div className="trial-banner">
              <div>
                <strong>Najpierw sprawdź na swoim boisku.</strong>
                <span>3 dni testów za 0 zł. Bez karty kredytowej.</span>
              </div>
              <Link to="/register">
                Wypróbuj Turnivo <span aria-hidden="true">↗</span>
              </Link>
            </div>
          </div>
        </section>

        <section className="final-cta wrap" ref={finalRef}>
          <span className="eyebrow">GOTOWY NA PIERWSZY GWIZDEK?</span>
          <h2>
            NIECH WYGRA
            <br />
            <span>SPORT.</span>
          </h2>
          <div>
            <p>
              Organizacja jest po naszej stronie.
              <br />
              Emocje należą do Was.
            </p>
            <Link className="button" to="/register">
              Stwórz swój turniej <span aria-hidden="true">↗</span>
            </Link>
          </div>
        </section>
      </main>

      <footer className="footer wrap">
        <div className="footer-top">
          <TurnivoLogo className="brand brand-portal" textSize="text-lg" boxSize="h-10 w-10" iconSize="w-5 h-5" />
          <p>Z pasji do gry. Z myślą o organizatorach.</p>
          <Link to="/wsparcie">
            Porozmawiajmy <span aria-hidden="true">↗</span>
          </Link>
        </div>
        <div className="footer-bottom">
          <span>© {new Date().getFullYear()} Turnivo</span>
          <div>
            <Link to="/polityka-prywatnosci">Polityka prywatności</Link>
            <Link to="/regulamin">Regulamin</Link>
            <Link to="/usuniecie-konta">Usunięcie konta</Link>
            <Link to="/wsparcie">Wsparcie</Link>
          </div>
          <a
            href="https://www.pexels.com/video/people-playing-volleyball-in-gymnasium-6217182/"
            target="_blank"
            rel="noopener noreferrer"
          >
            Film: Pavel Danilyuk / Pexels ↗
          </a>
          <a
            href="#main"
            onClick={(event) => {
              event.preventDefault();
              window.scrollTo({ top: 0, behavior: motionPaused ? 'instant' : 'smooth' });
            }}
          >
            Do góry ↑
          </a>
        </div>
      </footer>
    </div>
  );
}
